<template>
  <div :id="wrapperId" class="content-custom-html">
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div
      v-if="sanitizedHtml"
      data-spa-links="false"
      data-testid="content"
      v-html="sanitizedHtml"
    />
    <SAlert
      v-if="showErrorDetails && notices.length > 0"
      class="content-custom-html__notice"
      color="warning"
    >
      <p v-for="notice in notices" :key="notice">{{ notice }}</p>
    </SAlert>
    <SAlert
      v-if="jsErrorMessage"
      class="content-custom-html__error"
      color="danger"
    >
      {{ jsErrorMessage }}
    </SAlert>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from "#app";
import { computed, onMounted, ref, useId } from "vue";

import { SAlert } from "@solvari/common-fe";
import { getEnv, getSentry } from "@solvari/utils";

import { customHtmlNotices } from "~/shared/libs/customHtmlNotices.ts";
import {
  repairCustomCss,
  repairCustomHtml,
} from "~/shared/libs/customHtmlRepair.ts";

const props = defineProps<{
  css: string | null;
  html: string | null;
  js: string | null;
}>();

const wrapperId = `content-custom-html-${useId()}`;

const route = useRoute();
const jsErrorMessage = ref<string | null>(null);

/** Only an editor can act on a notice, so visitors never see one. */
const showErrorDetails = computed(
  () => getEnv().config.envType !== "production" || !!route.query.preview,
);

const notices = computed(() => customHtmlNotices(props.html, props.css));

const scopedStyleTag = computed(() => {
  const css = repairCustomCss(props.css);
  if (!css) {
    return "";
  }
  return `<style>#${wrapperId} { ${css} }</style>`;
});

/** A block may build its own DOM in JS, so css alone must still render. */
const sanitizedHtml = computed(() => {
  const markup = `${repairCustomHtml(props.html) ?? ""}${scopedStyleTag.value}`;
  return markup === "" ? null : markup;
});

onMounted(() => {
  if (!props.js) {
    return;
  }
  try {
    new Function(props.js)();
  } catch (error) {
    getSentry({ tags: { project: "main-site-mkt" } }).captureException(error);
    if (showErrorDetails.value) {
      jsErrorMessage.value =
        error instanceof Error ? error.message : String(error);
    }
  }
});
</script>

<style lang="postcss">
.content-custom-html {
  @apply !font-primary;

  &__notice,
  &__error {
    @apply mt-4;
  }
}
</style>
